import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkle, TrendUp, Buildings, Brain } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { STAGES } from "@/lib/stages";

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/dashboard/stats")
            .then((r) => setStats(r.data))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="overline text-muted-foreground">Dashboard</div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black mt-2 leading-none">
                        Growth, at a glance.
                    </h1>
                </div>
                <Link to="/discover" className="btn-ai" data-testid="dashboard-discover-btn">
                    <Sparkle size={16} weight="fill" /> Discover Leads
                </Link>
            </div>

            {/* Stat row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={<Buildings size={20} />}
                    label="Total Leads"
                    value={loading ? "…" : stats?.total_leads ?? 0}
                    accent="text-foreground"
                    testid="stat-total"
                />
                <StatCard
                    icon={<Brain size={20} />}
                    label="AI Researched"
                    value={loading ? "…" : stats?.researched_count ?? 0}
                    accent="text-signal-ai"
                    testid="stat-researched"
                />
                <StatCard
                    icon={<TrendUp size={20} />}
                    label="Research In Progress"
                    value={loading ? "…" : (stats?.by_stage?.research_in_progress || 0)}
                    accent="text-brand"
                    testid="stat-pipeline"
                />
                <StatCard
                    icon={<Sparkle size={20} weight="fill" />}
                    label="Sent to CRM"
                    value={loading ? "…" : stats?.by_stage?.sent_to_crm ?? 0}
                    accent="text-signal-success"
                    testid="stat-won"
                />
            </div>

            {/* Stage breakdown */}
            <div className="card-os p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="overline text-muted-foreground">Pipeline distribution</div>
                        <h2 className="font-heading text-2xl font-bold mt-1">Where your leads live</h2>
                    </div>
                    <Link to="/pipeline" className="text-sm text-brand font-medium inline-flex items-center gap-1 hover:underline" data-testid="open-pipeline">
                        Open board <ArrowUpRight size={14} />
                    </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {STAGES.map((s) => (
                        <div key={s.key} className="border border-border rounded-sm p-3" data-testid={`stage-card-${s.key}`}>
                            <div className={`h-1 w-8 ${s.color} rounded-sm mb-2`} />
                            <div className="overline text-muted-foreground">{s.label}</div>
                            <div className="font-heading text-3xl font-black mt-1">
                                {loading ? "…" : stats?.by_stage?.[s.key] ?? 0}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Recent leads */}
            <div className="card-os">
                <div className="flex items-center justify-between p-6 pb-3 border-b border-border">
                    <div>
                        <div className="overline text-muted-foreground">Recent activity</div>
                        <h2 className="font-heading text-2xl font-bold mt-1">Newest leads</h2>
                    </div>
                </div>
                <div className="divide-y divide-border" data-testid="recent-leads">
                    {loading && <div className="p-6 text-muted-foreground text-sm">Loading…</div>}
                    {!loading && (!stats?.recent_leads?.length) && (
                        <div className="p-10 text-center">
                            <p className="text-muted-foreground text-sm">No leads yet. Start by discovering companies.</p>
                            <Link to="/discover" className="btn-ai mt-4 inline-flex" data-testid="empty-discover-btn">
                                <Sparkle size={16} weight="fill" /> Discover Leads
                            </Link>
                        </div>
                    )}
                    {!loading && stats?.recent_leads?.map((l) => (
                        <Link key={l.id} to={`/leads/${l.id}`}
                            className="flex items-center justify-between p-4 hover:bg-muted transition-colors"
                            data-testid={`recent-lead-${l.id}`}>
                            <div className="min-w-0">
                                <div className="font-medium truncate">{l.company_name}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {l.industry} · {l.location}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {l.insights?.lead_score != null && (
                                    <span className="chip border-signal-ai text-signal-ai" data-testid={`score-chip-${l.id}`}>
                                        Score {l.insights.lead_score}
                                    </span>
                                )}
                                <span className="overline text-muted-foreground">{l.stage}</span>
                                <ArrowUpRight size={16} className="text-muted-foreground" />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StatCard({ icon, label, value, accent, testid }) {
    return (
        <div className="card-os p-5" data-testid={testid}>
            <div className="flex items-center justify-between">
                <span className="overline text-muted-foreground">{label}</span>
                <span className={accent}>{icon}</span>
            </div>
            <div className={`font-heading text-4xl font-black mt-3 leading-none ${accent}`}>{value}</div>
        </div>
    );
}
